import React, { useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, LabelList, Legend,
} from "recharts";
import { Download, FileText, Users, Search } from "lucide-react";
import { api } from "@/lib/api";
import PersonCard from "@/components/election/PersonCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const PRED_LABEL = {
  KESIN: "Kesin bizden", BELKI: "Belki", BELIRSIZ: "Belirsiz", GELMEYECEK: "Gelmeyecek",
  ULASILAMADI: "Ulaşılamadı", KARSI: "Karşı", "": "Boş",
};
const OUT = [
  ["biz", "Bize verdi", "#16A34A"],
  ["karsi", "Karşıya verdi", "#DC2626"],
  ["belirsiz", "Belirsiz", "#F59E0B"],
  ["gelmedi", "Gelmedi", "#64748B"],
];
const outcomeOf = (r) => (!r.marked ? "gelmedi"
  : r.tally_group === "biz" ? "biz" : r.tally_group === "karsi" ? "karsi" : "belirsiz");

const Box = ({ label, value, sub, color, testid }) => (
  <div className="rounded-lg border border-border p-3" data-testid={testid}
    style={{ borderLeftWidth: 4, borderLeftColor: color }}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-0.5 font-display text-2xl font-bold" style={{ color }}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

export default function CommitteeDetail({ data, cno }) {
  const [tab, setTab] = useState("biz");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [person, setPerson] = useState(null);
  const c = data.committees[0];
  if (!c) return null;
  const g = data.groups || {};
  const mx = data.matrix;

  const dl = async (format) => {
    setBusy(true);
    try {
      const res = await api.get("/election-day/reconcile/committee-report", {
        params: { cno: Number(cno), format }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KOMITE_K${cno}_RAPOR.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally {
      setBusy(false);
    }
  };

  const pie = OUT.map(([k, label, color]) => ({ name: label, value: g[k] || 0, color }));
  const bars = [
    { name: "2026 Tahmini", v: c.forecast_biz, color: "#0F2C59" },
    { name: "Kötü Senaryo", v: c.worst_biz, color: "#F59E0B" },
    { name: "Çetele", v: c.tally_biz, color: "#16A34A" },
    ...(c.has_ballot ? [{ name: "Resmî Sandık", v: c.ballot_our, color: "#111827" }] : []),
  ];
  const rows = data.rows.filter((r) => {
    if (tab !== "all" && outcomeOf(r) !== tab) return false;
    if (q) {
      const t = `${r.company_name} ${r.sicil_no} ${r.voting_person}`.toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const tabs = [...OUT.map(([k, label]) => [k, `${label} (${g[k] || 0})`]),
    ["all", `Tüm firmalar (${data.rows.length})`]];

  return (
    <div className="space-y-4" data-testid="committee-detail">
      {person && <PersonCard name={person} onClose={() => setPerson(null)} />}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold">
              K{c.committee_no} · {c.name}
            </h3>
            <p className="text-xs text-muted-foreground">
              {c.faal} faal firma · {c.marked} firma sandığa geldi (katılım %{c.participation}) ·
              {" "}meclis koltuğu {c.seats}
            </p>
          </div>
          <Button size="sm" disabled={busy} onClick={() => dl("pdf")} data-testid="com-report-pdf">
            <Download className="mr-1.5 h-4 w-4" /> Komite Raporu (PDF)
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => dl("xlsx")}
            data-testid="com-report-xlsx">
            <Download className="mr-1.5 h-4 w-4" /> Komite Raporu (Excel)
          </Button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {OUT.map(([k, label, color]) => (
            <Box key={k} testid={`com-box-${k}`} label={label} value={g[k] || 0} color={color}
              sub={c.faal ? `tüm firmaların %${Math.round(((g[k] || 0) / c.faal) * 100)}` : ""} />
          ))}
        </div>

        <div className="mt-3 rounded-lg bg-muted/50 p-3 text-sm" data-testid="com-verdict">
          <b>Bir bakışta:</b> Tahminimizde <b>{c.pred_kesin}</b> firma kesin bizdendi;
          bunlardan <b>{c.kesin_gelmeyen}</b> tanesi sandığa hiç gelmedi.
          Çetelede bize <b>{c.tally_biz}</b>, karşıya <b>{c.tally_karsi}</b>,
          belirsiz <b>{c.tally_belki}</b> oy işaretlendi.
          {c.has_ballot
            ? <> Resmî sandık sonucu bize <b>{c.ballot_our}</b>, karşıya <b>{c.ballot_rival ?? "—"}</b> —
              komite <b>{c.winner === "biz" ? "BİZDE" : c.winner === "karsi" ? "RAKİPTE" : "—"}</b>.</>
            : " Resmî sandık sonucu henüz girilmedi."}
          {" "}Tahmin {c.dev_forecast > 0 ? `${c.dev_forecast} oy fazla` : c.dev_forecast < 0
            ? `${Math.abs(c.dev_forecast)} oy eksik` : "birebir"} çıktı,
          tutarlılık <b>%{c.score_forecast}</b>.
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4" data-testid="com-pie">
          <h3 className="text-sm font-bold">Sandık davranışı — tüm faal firmalar</h3>
          <div className="mt-2 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95}
                  label={({ name, value, percent }) =>
                    value ? `${name}: ${value} (%${Math.round(percent * 100)})` : ""}
                  labelLine={false}>
                  {pie.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4" data-testid="com-bars">
          <h3 className="text-sm font-bold">Bize gelen oy — tahmin / kötü senaryo / çetele / sandık</h3>
          <div className="mt-2 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bars} margin={{ top: 18, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="v" name="Bize oy" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="v" position="top" fontSize={12} fontWeight="bold" />
                  {bars.map((b) => <Cell key={b.name} fill={b.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {mx && (
        <Card className="p-4" data-testid="com-matrix">
          <h3 className="text-sm font-bold">Nerede yanıldık? — tahmin × gerçekleşen</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Satır: seçim öncesi tahminimiz · Kolon: sandıkta gerçekte ne oldu.
            Yeşil hücreler doğru tahmin, kırmızı hücreler hatalı tahmindir.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left">
                  <th className="px-2 py-1.5 font-semibold">2026 Tahmini</th>
                  {mx.outcomes.map((o) => (
                    <th key={o.key} className="px-2 py-1.5 text-center font-semibold">{o.label}</th>
                  ))}
                  <th className="px-2 py-1.5 text-center font-semibold">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {mx.preds.map((pr) => (
                  <tr key={pr.key} className="border-b border-border/60"
                    data-testid={`matrix-row-${pr.key || "bos"}`}>
                    <td className="px-2 py-1.5 font-medium">{pr.label}</td>
                    {mx.outcomes.map((o) => {
                      const v = pr.cells[o.key] || 0;
                      const good = (pr.key === "KESIN" && o.key === "biz")
                        || (pr.key === "KARSI" && o.key === "karsi")
                        || (pr.key === "GELMEYECEK" && o.key === "gelmedi")
                        || (pr.key === "BELIRSIZ" && o.key === "belirsiz");
                      const bad = ["KESIN", "BELKI"].includes(pr.key)
                        && ["karsi", "gelmedi"].includes(o.key);
                      return (
                        <td key={o.key} className="px-2 py-1.5 text-center font-semibold"
                          style={{
                            background: !v ? "transparent"
                              : good ? "rgba(22,163,74,0.18)"
                                : bad ? "rgba(220,38,38,0.18)" : "rgba(148,163,184,0.14)",
                          }}>
                          {v || ""}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center font-bold">{pr.total}</td>
                  </tr>
                ))}
                <tr className="bg-muted font-bold">
                  <td className="px-2 py-1.5">TOPLAM</td>
                  {mx.outcomes.map((o) => (
                    <td key={o.key} className="px-2 py-1.5 text-center">{mx.totals[o.key]}</td>
                  ))}
                  <td className="px-2 py-1.5 text-center">{mx.grand}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="p-4" data-testid="com-voter-lists">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex flex-1 items-center gap-1.5 text-sm font-bold">
            <Users className="h-4 w-4" /> Seçmen listeleri
          </h3>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma / kişi ara"
              data-testid="com-search"
              className="h-9 w-[200px] rounded-md border border-border bg-background pl-7 pr-2 text-xs" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              data-testid={`com-tab-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                tab === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
              {label}
            </button>
          ))}
        </div>
        <ScrollArea className="mt-3 h-[520px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                {["Firma", "Oy kullanacak kişi", "Oy gücü", "2026 Tahmini", "Çetele", "Uyum",
                  "İşaretleyen", "Neden / Not"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const o = outcomeOf(r);
                const col = OUT.find(([k]) => k === o)[2];
                return (
                  <tr key={r.id} className="border-b border-border/60"
                    data-testid={`com-row-${r.id}`}>
                    <td className="max-w-[240px] px-2 py-1.5">
                      <span className="block truncate font-medium"
                        style={{ borderLeft: `3px solid ${col}`, paddingLeft: 6 }}>
                        {r.company_name}
                      </span>
                      <span className="pl-2 text-[10px] text-muted-foreground">{r.sicil_no}</span>
                    </td>
                    <td className="max-w-[150px] px-2 py-1.5">
                      {r.voting_person ? (
                        <button type="button" onClick={() => setPerson(r.voting_person)}
                          data-testid={`person-open-${r.id}`}
                          className="block max-w-full truncate text-left font-medium text-sky-700 hover:underline dark:text-sky-400">
                          {r.voting_person}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.vote_power > 1
                        ? <span className="rounded bg-[#0F2C59] px-1.5 py-0.5 font-bold text-white">{r.vote_power}</span>
                        : (r.vote_power || "—")}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">{PRED_LABEL[r.prediction] ?? r.prediction}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-semibold" style={{ color: col }}>
                      {r.marked ? (PRED_LABEL[r.tally] ?? r.tally ?? "—") : "Gelmedi"}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {r.match === "UYUM" ? "Uyumlu" : r.match === "SAPMA" ? "Sapma"
                        : r.match === "BEKLEYEN" ? "—" : "Eğilim yok"}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground">
                      {r.marked_by || "—"} {r.marked_at ? String(r.marked_at).slice(11, 16) : ""}
                    </td>
                    <td className="max-w-[180px] truncate px-2 py-1.5 text-muted-foreground">
                      {(r.reason || "") + (r.note ? ` · ${r.note}` : "") || "—"}
                    </td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr><td colSpan={8} className="px-2 py-3 text-muted-foreground">
                  Bu grupta kayıt yok.
                </td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
        <p className="mt-2 text-xs text-muted-foreground">
          <FileText className="mr-1 inline h-3.5 w-3.5" />
          Komite Raporu PDF'inde bu dört liste (bize verenler, karşıya verenler, belirsiz,
          gelmeyenler) tam haliyle yer alır; Excel'de ayrıca her grup kendi sayfasında.
        </p>
      </Card>
    </div>
  );
}
